// src/lib/liveTracker.js
// Maison D'Oud's own first-party analytics: live visitors, the page each one
// is on, how long they spend there, and the shopping funnel. Events go to our
// own Netlify function (/.netlify/functions/track), which adds the visitor's
// approximate city from Netlify's edge geolocation and stores it in Supabase.
//
// Rules this module follows so it can never affect the rest of the site:
//   - It does nothing unless REACT_APP_LIVE_ANALYTICS is "on" (Netlify env var).
//   - It only runs after the visitor allows Analytics cookies. analytics.js
//     starts and stops it from applyConsent(); it never reads consent itself.
//   - Every send is fire-and-forget (sendBeacon / keepalive fetch). Failures are
//     swallowed; nothing here can throw into React or delay navigation.
//   - Admin pages (/admin…) are never tracked.

const ENABLED = process.env.REACT_APP_LIVE_ANALYTICS === 'on';
const ENDPOINT = '/.netlify/functions/track';
const HEARTBEAT_MS = 15 * 1000;          // while the tab is visible
const MAX_PAGE_MS = 30 * 60 * 1000;      // cap one page's time at 30 minutes
const SESSION_KEY = 'md_live_session';   // sessionStorage: { id, startedAt, pages }
const VISITOR_KEY = 'md_live_visitor';   // localStorage: random id for returning visitors

let started = false;
let getCampaign = () => null;
let heartbeatTimer = null;
let session = null;          // { id, startedAt, pages }
let visitorId = '';
let pendingPath = null;      // a page viewed before consent / before start

// Current page state
let page = null;             // { path, enteredAt, visibleMs, visibleSince, maxScroll }
let stage = 'browsing';
let cart = { items: null, value: null, currency: null };

// ─── Small helpers ──────────────────────────────────────────────────────────

function storage(type) {
    try {
        return window[type];
    } catch {
        return null;
    }
}

function randomId() {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch {
        // fall through
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function loadSession() {
    try {
        const raw = storage('sessionStorage')?.getItem(SESSION_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.id && parsed?.startedAt) return parsed;
    } catch {
        // ignore
    }
    return { id: randomId(), startedAt: new Date().toISOString(), pages: 0 };
}

function saveSession() {
    try {
        storage('sessionStorage')?.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
        // ignore — tracking is best-effort
    }
}

function loadVisitorId() {
    const store = storage('localStorage');
    try {
        let id = store?.getItem(VISITOR_KEY);
        if (!id) {
            id = randomId();
            store?.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        return '';
    }
}

function isAdminPath(path) {
    return typeof path === 'string' && path.startsWith('/admin');
}

function deviceType() {
    try {
        const width = window.innerWidth || 1024;
        if (width < 768) return 'mobile';
        if (width < 1024) return 'tablet';
        return 'desktop';
    } catch {
        return 'desktop';
    }
}

function stageForPath(path) {
    if (path && path.startsWith('/product/')) return 'product';
    return 'browsing';
}

function scrollPercent() {
    try {
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return 100;
        return Math.max(0, Math.min(100, Math.round((window.scrollY / scrollable) * 100)));
    } catch {
        return 0;
    }
}

function visibleMsSoFar() {
    if (!page) return 0;
    let ms = page.visibleMs;
    if (page.visibleSince) ms += Date.now() - page.visibleSince;
    return Math.min(ms, MAX_PAGE_MS);
}

function send(payload) {
    let body;
    try {
        body = JSON.stringify(payload);
    } catch {
        return;
    }
    try {
        // text/plain keeps this a "simple" request; the function parses the JSON.
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))) {
            return;
        }
    } catch {
        // fall back to fetch
    }
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            body,
            keepalive: true,
            credentials: 'omit',
            headers: { 'Content-Type': 'text/plain' },
        }).catch(() => {});
    } catch {
        // ignore
    }
}

// Everything the live map needs, sent with every event and heartbeat.
function livePayload() {
    const campaign = (() => {
        try {
            return getCampaign() || {};
        } catch {
            return {};
        }
    })();
    return {
        session_id: session.id,
        visitor_id: visitorId,
        started_at: session.startedAt,
        pages_viewed: session.pages,
        path: page?.path || null,
        page_title: (typeof document !== 'undefined' && document.title) ? document.title.slice(0, 120) : null,
        page_entered_at: page ? new Date(page.enteredAt).toISOString() : null,
        scroll_pct: page ? page.maxScroll : null,
        stage,
        cart_items: cart.items,
        cart_value: cart.value,
        currency: cart.currency,
        device: deviceType(),
        referrer: campaign.referrer || null,
        utm_source: campaign.utm_source || null,
        utm_medium: campaign.utm_medium || null,
        utm_campaign: campaign.utm_campaign || null,
    };
}

function sendEvent(event, extra = {}) {
    if (!started || !session || (page && isAdminPath(page.path))) return;
    send({ type: 'event', event, ...livePayload(), ...extra });
}

// Tells the server this visitor has gone, so they drop off the live list at once.
// Sent when the tab is hidden (switching apps, locking the phone, closing) and on
// pagehide. If they come back, the next heartbeat puts them straight back on.
function sendLeave() {
    if (!started || !session) return;
    send({ type: 'leave', session_id: session.id });
}

function sendHeartbeat() {
    if (!started || !session || !page || isAdminPath(page.path)) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    send({ type: 'heartbeat', ...livePayload() });
}

// Records how long the current page was visible, then forgets it.
function finishPage() {
    if (!page) return;
    const duration = visibleMsSoFar();
    if (duration >= 1000 && !isAdminPath(page.path)) {
        sendEvent('page_time', { duration_ms: duration, props: { scroll: page.maxScroll } });
    }
    page = null;
}

function beginPage(path) {
    finishPage();
    const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
    page = {
        path,
        enteredAt: Date.now(),
        visibleMs: 0,
        visibleSince: visible ? Date.now() : null,
        maxScroll: scrollPercent(),
    };
    if (isAdminPath(path)) return;
    session.pages += 1;
    saveSession();
    if (stage !== 'checkout') stage = stageForPath(path);
    sendEvent('page_view');
}

// ─── Browser listeners ──────────────────────────────────────────────────────

function onVisibilityChange() {
    if (!page) return;
    if (document.visibilityState === 'visible') {
        if (!page.visibleSince) page.visibleSince = Date.now();
        sendHeartbeat();
    } else {
        if (page.visibleSince) {
            page.visibleMs += Date.now() - page.visibleSince;
            page.visibleSince = null;
        }
        // Leaving the tab may be the last chance to record this page's time.
        // One request does both: saves the time on page and removes them from
        // the live list (leave: true), so the two can't arrive out of order.
        const duration = visibleMsSoFar();
        if (duration >= 1000 && !isAdminPath(page.path)) {
            sendEvent('page_time', { duration_ms: duration, props: { scroll: page.maxScroll }, leave: true });
            // Count only time after the visitor comes back, so it isn't recorded twice.
            page.visibleMs = 0;
        } else {
            sendLeave();
        }
    }
}

function onPageHide() {
    sendLeave();
}

let scrollQueued = false;
function onScroll() {
    if (scrollQueued || !page) return;
    scrollQueued = true;
    window.requestAnimationFrame(() => {
        scrollQueued = false;
        if (page) page.maxScroll = Math.max(page.maxScroll, scrollPercent());
    });
}

function addListeners() {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('scroll', onScroll, { passive: true });
}

function removeListeners() {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('scroll', onScroll);
}

// ─── Public API (called only from analytics.js) ─────────────────────────────

// Starts tracking. Called when the visitor has allowed Analytics cookies.
export function startLiveTracking(options = {}) {
    if (!ENABLED || started || typeof window === 'undefined') return;
    try {
        if (typeof options.getCampaign === 'function') getCampaign = options.getCampaign;
        session = loadSession();
        visitorId = loadVisitorId();
        started = true;
        addListeners();
        heartbeatTimer = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
        // The page they were on when they accepted cookies.
        const path = pendingPath || window.location.pathname;
        pendingPath = null;
        beginPage(path);
    } catch {
        started = false;
    }
}

// Stops tracking and forgets this browser's ids. Called if consent is withdrawn.
export function stopLiveTracking() {
    if (!started) return;
    try {
        finishPage();
        started = false;
        if (heartbeatTimer) window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
        removeListeners();
        storage('sessionStorage')?.removeItem(SESSION_KEY);
        storage('localStorage')?.removeItem(VISITOR_KEY);
    } catch {
        // ignore
    }
    session = null;
    visitorId = '';
    page = null;
    stage = 'browsing';
    cart = { items: null, value: null, currency: null };
}

// Route change. Before tracking starts, just remembers the latest path.
export function livePageView(path) {
    if (!ENABLED) return;
    try {
        if (!started) {
            pendingPath = path;
            return;
        }
        if (page && page.path === path) return;
        beginPage(path);
    } catch {
        // ignore
    }
}

// Shopping and quiz events. `data` may include product_handle, value, currency,
// cart ({ items, value, currency }) and props.
export function liveEvent(event, data = {}) {
    if (!ENABLED || !started) return;
    try {
        if (data.cart) {
            cart = {
                items: Number.isFinite(data.cart.items) ? data.cart.items : null,
                value: Number.isFinite(data.cart.value) ? data.cart.value : null,
                currency: data.cart.currency || null,
            };
        }
        if (event === 'begin_checkout') stage = 'checkout';
        else if ((event === 'add_to_cart' || event === 'view_cart') && stage !== 'checkout') stage = 'cart';
        sendEvent(event, {
            product_handle: data.product_handle || null,
            value: Number.isFinite(data.value) ? data.value : null,
            event_currency: data.currency || null,
            props: data.props || {},
        });
    } catch {
        // ignore
    }
}

// The id saved on the Shopify order so purchases link back to the visit.
export function liveSessionId() {
    return ENABLED && started && session ? session.id : '';
}
